/**
 * OLMPS Spectrum Engine — data layer.
 *
 * Bridges the pure math (spectrum.ts) to Supabase: loads state, derives reps
 * from the tools' own telemetry tables, runs the idempotent weekly tally, and
 * persists reveals. Same direct-`supabase` pattern as today.ts / flo.ts.
 *
 * Phase 3 wires THEMIS end-to-end; Phase 4 adds the remaining starter five by
 * writing more `RepDeriver`s and registering them in DERIVERS.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  computeReveal,
  initialState,
  type Rep,
  type RevealHistory,
  type SpectrumState,
  SECURE_CONSECUTIVE_REVEALS,
} from './spectrum';
import { getTool, normalizeMaladyId, type DmtToolId } from './spectrum-tools';
import { DEFAULT_GAME_SETTINGS, isWeekBoundaryEvening, weekStartOf, type GameSettings } from './gameSettings';

// ---------------------------------------------------------------------------
// Week boundaries (user-configurable; default closes Sunday evening). Dependency-free (no date-fns) so
// this file is portable to the mobile app, which does not bundle date-fns.
// ---------------------------------------------------------------------------

/** Local midnight that opens the week containing `d`. Weeks follow the user's
 *  week boundary setting (default: the week closes Sunday evening, so it opens
 *  Monday 00:00) — see gameSettings.weekStartOf. */
export function weekStart(d: Date, game: GameSettings = DEFAULT_GAME_SETTINGS): Date {
  return weekStartOf(d, game);
}

/** `yyyy-MM-dd` (local) — used for `dmt_reveals.week_start` and date columns. */
function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** `yyyy-MM-dd` of a week's opening day — the `dmt_reveals.week_start` key. */
export function weekKey(mondayStart: Date): string {
  return ymd(mondayStart);
}

/** Add N weeks (local). */
function addWeeksLocal(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n * 7);
  return x;
}

// ---------------------------------------------------------------------------
// State load / persist
// ---------------------------------------------------------------------------

interface DmtStateRow {
  tool_id: string;
  rolling_score: number;
  displayed_position: number;
  rep_count: number;
  acquisition_status: string;
  maintenance_status: string | null;
  malady_subtype: string | null;
  floor: number | null;
  secured_at: string | null;
  intake_position: number | null;
  certified: boolean | null;
}

function rowToState(row: DmtStateRow): SpectrumState {
  return {
    rolling: Number(row.rolling_score),
    displayed: row.displayed_position,
    repCount: row.rep_count,
    acquisition: row.acquisition_status as SpectrumState['acquisition'],
    maintenance: (row.maintenance_status as SpectrumState['maintenance']) ?? null,
    floor: row.floor,
    securedAt: row.secured_at,
  };
}

/** Load one tool's state, seeding from intake position when the row is absent. */
export async function loadState(
  supabase: SupabaseClient,
  userId: string,
  toolId: DmtToolId,
): Promise<{
  state: SpectrumState;
  maladySubtype: string | null;
  intakePosition: number | null;
  certified: boolean;
}> {
  const { data } = await supabase
    .from('dmt_state')
    .select(
      'tool_id, rolling_score, displayed_position, rep_count, acquisition_status, maintenance_status, malady_subtype, floor, secured_at, intake_position, certified',
    )
    .eq('user_id', userId)
    .eq('tool_id', toolId)
    .maybeSingle();

  if (!data) {
    return { state: initialState(), maladySubtype: null, intakePosition: null, certified: false };
  }
  const row = data as DmtStateRow;
  return {
    state: rowToState(row),
    maladySubtype: row.malady_subtype ? normalizeMaladyId(toolId, row.malady_subtype) : row.malady_subtype,
    intakePosition: row.intake_position,
    certified: !!row.certified,
  };
}

async function persistState(
  supabase: SupabaseClient,
  userId: string,
  toolId: DmtToolId,
  state: SpectrumState,
): Promise<void> {
  await supabase.from('dmt_state').upsert(
    {
      user_id: userId,
      tool_id: toolId,
      rolling_score: state.rolling,
      displayed_position: state.displayed,
      rep_count: state.repCount,
      acquisition_status: state.acquisition,
      maintenance_status: state.maintenance,
      floor: state.floor,
      secured_at: state.securedAt,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'user_id,tool_id' },
  );
}

// ---------------------------------------------------------------------------
// Intake (Phase 1 self-report) + certification (Phase 2 practical check)
// ---------------------------------------------------------------------------

/** Whether this tool's intake has been taken. Drives showing the diagnostic. */
export async function hasIntake(supabase: SupabaseClient, userId: string, toolId: DmtToolId): Promise<boolean> {
  const { data } = await supabase
    .from('dmt_intake')
    .select('tool_id')
    .eq('user_id', userId)
    .eq('tool_id', toolId)
    .limit(1);
  return !!data && data.length > 0;
}

/**
 * Save an intake result: record the answers, lock the subtype, and record the
 * start position as CONTEXT (how entrenched the malady is). The seat itself
 * starts at 0 (Update Directive 18 Oct 2026 §5): displayed_position/rolling_score
 * are written as 0, never seeded from the intake — the seat score fills only
 * from real marks (seatScore.ts) and nothing reads displayed_position for it.
 */
export async function saveIntake(
  supabase: SupabaseClient,
  userId: string,
  toolId: DmtToolId,
  answers: unknown,
  subtype: string,
  startPosition: number,
): Promise<void> {
  await supabase.from('dmt_intake').insert({
    user_id: userId,
    tool_id: toolId,
    answers,
    subtype,
    start_position: startPosition,
  });

  const seeded = initialState(); // seat starts at 0 — intake is context only
  await supabase.from('dmt_state').upsert(
    {
      user_id: userId,
      tool_id: toolId,
      rolling_score: seeded.rolling,
      displayed_position: seeded.displayed,
      rep_count: 0,
      acquisition_status: seeded.acquisition,
      maintenance_status: null,
      malady_subtype: subtype,
      floor: null,
      secured_at: null,
      intake_position: startPosition,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'user_id,tool_id' },
  );
}

/** Mark a tool's certification challenge passed (one of the four securing gates). */
export async function markCertified(supabase: SupabaseClient, userId: string, toolId: DmtToolId): Promise<void> {
  await supabase
    .from('dmt_state')
    .upsert(
      { user_id: userId, tool_id: toolId, certified: true, updated_at: new Date().toISOString() },
      { onConflict: 'user_id,tool_id' },
    );
}

// ---------------------------------------------------------------------------
// Rep derivation
// ---------------------------------------------------------------------------

/**
 * A RepDeriver reads a tool's own telemetry for [from, to) and returns the
 * graded reps for that window. Returns [] for a genuine no-opportunity week
 * (which the math treats as "no tally", never a 0).
 */
export type RepDeriver = (
  supabase: SupabaseClient,
  userId: string,
  from: Date,
  to: Date,
) => Promise<Rep[]>;

/**
 * THEMIS (Dysnomia) — protocol fidelity. Reads `protocol_completions`:
 *   +2  completed under friction (run resumed after ≥1 pause)
 *   +1  completed cleanly, no repeated steps
 *   −1  abandoned, or completed but with repeated steps (order broke down)
 * No completed runs in the window → no reps (no-opportunity week).
 * Silent-skip / −2 malady-control detection is Phase 7 (needs step-level diff).
 */
export const deriveThemisReps: RepDeriver = async (supabase, userId, from, to) => {
  const { data } = await supabase
    .from('protocol_completions')
    .select('status, steps_repeated, pause_count, completed_at, started_at')
    .eq('user_id', userId)
    .gte('started_at', from.toISOString())
    .lt('started_at', to.toISOString());

  const rows = (data ?? []) as Array<{
    status: string | null;
    steps_repeated: Record<string, number> | null;
    pause_count: number | null;
  }>;

  const reps: Rep[] = [];
  for (const r of rows) {
    if (r.status === 'abandoned') {
      reps.push({ grade: -1, friction: false });
      continue;
    }
    if (r.status === 'success') {
      const repeated = r.steps_repeated
        ? Object.values(r.steps_repeated).reduce((a, b) => a + (b || 0), 0)
        : 0;
      if (repeated > 0) {
        reps.push({ grade: -1, friction: false }); // ran, but the order didn't hold
      } else if ((r.pause_count ?? 0) > 0) {
        reps.push({ grade: 2, friction: true }); // held the protocol through interruption
      } else {
        reps.push({ grade: 1, friction: false }); // clean run
      }
    }
    // any other status → not a gradable opportunity
  }
  return reps;
};

/**
 * KYRS (Koalemos/Oknos) — did you work the highest-priority task first?
 * Reads `tasks` done in the window, ordered by completion (`updated_at`), and
 * checks whether each completion was among the top-priority open work at the
 * time. Economical proxy: compare the completed task's priority against the
 * median priority of tasks open that week — completing high-priority = +1,
 * completing while higher-priority work rotted = −1. No completions → no reps.
 */
export const deriveKyrsReps: RepDeriver = async (supabase, userId, from, to) => {
  const { data: doneData } = await supabase
    .from('tasks')
    .select('id, default_priority, done, updated_at, unlisted')
    .eq('user_id', userId)
    .eq('done', true)
    .gte('updated_at', from.toISOString())
    .lt('updated_at', to.toISOString());

  const done = ((doneData ?? []) as Array<{ default_priority: number | null; unlisted: boolean | null }>).filter(
    (t) => !t.unlisted,
  );
  if (done.length === 0) return [];

  // Priorities of everything that was open during the window (created before
  // `to`, not yet done or done inside window) — the competition set.
  const { data: openData } = await supabase
    .from('tasks')
    .select('default_priority, created_at, unlisted')
    .eq('user_id', userId)
    .lt('created_at', to.toISOString());
  const pool = ((openData ?? []) as Array<{ default_priority: number | null; unlisted: boolean | null }>)
    .filter((t) => !t.unlisted)
    .map((t) => t.default_priority ?? 0)
    .sort((a, b) => a - b);
  const median = pool.length ? pool[Math.floor(pool.length / 2)] : 0;

  const reps: Rep[] = [];
  for (const t of done) {
    const p = t.default_priority ?? 0;
    if (p >= median) reps.push({ grade: 1, friction: false });
    else reps.push({ grade: -1, friction: false });
  }
  return reps;
};

/**
 * CLIO (Lethe) — did the day get closed and learned from? Reads
 * `journal_entries` in the window. Each day with a session present = +1; a day
 * with BOTH a morning and night session = +2 (the full loop held). Weeks with
 * no entries at all → no reps (no-opportunity, never punished). We cannot see
 * "days that were due but skipped" without a schedule, so absence is neutral.
 */
export const deriveClioReps: RepDeriver = async (supabase, userId, from, to) => {
  const { data } = await supabase
    .from('journal_entries')
    .select('date, entry_type_id')
    .eq('user_id', userId)
    .gte('date', ymd(from))
    .lt('date', ymd(to));

  const rows = (data ?? []) as Array<{ date: string; entry_type_id: string | null }>;
  if (rows.length === 0) return [];

  const byDay = new Map<string, Set<string>>();
  for (const r of rows) {
    if (!byDay.has(r.date)) byDay.set(r.date, new Set());
    byDay.get(r.date)!.add((r.entry_type_id ?? '').toLowerCase());
  }

  const reps: Rep[] = [];
  for (const [, types] of byDay) {
    const morning = [...types].some((t) => t.includes('morn') || t.includes('am'));
    const night = [...types].some((t) => t.includes('night') || t.includes('eve') || t.includes('pm'));
    if (morning && night) reps.push({ grade: 2, friction: true }); // full loop held
    else reps.push({ grade: 1, friction: false }); // at least one session
  }
  return reps;
};

// NUMA and TELOS have no derivers (ruling 1.3, no witness tool): their reps are
// the seat's own — NUMA's tap-a-stagnant-ball note, TELOS's declared-direction
// rep — plus the user's manual check-offs. Grading them from other tools' data
// (KYRS task throughput / task groups) is removed.

/**
 * METIS (Atē) — was the day planned and followed? Reads `agenda_items` for the
 * window: a day with items and a high completion share = +1 (or +2 if it held
 * through many items); a day with items but most abandoned = −1. Days with no
 * agenda built are neutral (no reps) — absence isn't punished.
 */
export const deriveMetisReps: RepDeriver = async (supabase, userId, from, to) => {
  const { data } = await supabase
    .from('agenda_items')
    .select('date, completed')
    .eq('user_id', userId)
    .gte('date', ymd(from))
    .lt('date', ymd(to));
  const rows = (data ?? []) as Array<{ date: string; completed: boolean | null }>;
  if (rows.length === 0) return [];

  const byDay = new Map<string, { total: number; done: number }>();
  for (const r of rows) {
    const d = byDay.get(r.date) ?? { total: 0, done: 0 };
    d.total += 1;
    if (r.completed) d.done += 1;
    byDay.set(r.date, d);
  }

  const reps: Rep[] = [];
  for (const [, d] of byDay) {
    const share = d.total ? d.done / d.total : 0;
    if (share >= 0.8 && d.total >= 4) reps.push({ grade: 2, friction: true }); // held a full day
    else if (share >= 0.5) reps.push({ grade: 1, friction: false });
    else reps.push({ grade: -1, friction: false }); // day fell apart from the plan
  }
  return reps;
};

/**
 * ARGOS (Achlys/Apate) — did you see where attention actually went, clearly and
 * without self-deception? Reads `argos_sessions` telemetry for the window. Any
 * tracked activity means the eye was open (+1); a week where a large share was
 * blocked/overridden (attention repeatedly off-plan and rationalised) = −1. No
 * telemetry captured → no reps (the extension may be off; not punished).
 */
export const deriveArgosReps: RepDeriver = async (supabase, userId, from, to) => {
  const { data } = await supabase
    .from('argos_sessions')
    .select('was_blocked, override_reason, started_at')
    .eq('user_id', userId)
    .gte('started_at', from.toISOString())
    .lt('started_at', to.toISOString());
  const rows = (data ?? []) as Array<{ was_blocked: boolean | null; override_reason: string | null }>;
  if (rows.length === 0) return [];

  const overrides = rows.filter((r) => r.was_blocked && r.override_reason).length;
  const overrideRatio = overrides / rows.length;
  // Reviewing your real activity at all is the trained act; heavy rationalised
  // overrides is Apate (self-deceit) winning.
  if (overrideRatio > 0.3) return [{ grade: -1, friction: false }];
  return [{ grade: 1, friction: false }];
};

/**
 * Manual evidence — MoyRi's "Used [deity] / [Malady] won" buttons write
 * `dmt_events` rows with source 'manual' (a situational mark, §4). They are
 * available for all five MoyRi seats (ATLAS, POROS, KYRS, METIS, CLIO); the
 * seat score (useSeats) reads them back.
 */
export const MANUAL_EVIDENCE_TOOLS: ReadonlySet<DmtToolId> = new Set<DmtToolId>(['telos', 'numa', 'kyrs', 'metis', 'clio']);

/**
 * Seats whose ONLY evidence trace is the manual log (TELOS/ATLAS, NUMA/POROS):
 * their deriver reads the manual events back as reps, so tallyToolWeek must not
 * re-insert them (they are already events). KYRS/METIS/CLIO also accept manual
 * marks but keep their telemetry deriver, which tallyToolWeek still persists.
 */
const MANUAL_ONLY_TOOLS: ReadonlySet<DmtToolId> = new Set<DmtToolId>(['telos', 'numa']);
export const isManualOnlyTool = (toolId: DmtToolId): boolean => MANUAL_ONLY_TOOLS.has(toolId);

/** Reads one tool's manual events back as reps. */
function manualDeriverFor(toolId: DmtToolId): RepDeriver {
  return async (supabase, userId, from, to) => {
    const { data } = await supabase
      .from('dmt_events')
      .select('grade, friction')
      .eq('user_id', userId)
      .eq('tool_id', toolId)
      .eq('source', 'manual')
      .gte('occurred_at', from.toISOString())
      .lt('occurred_at', to.toISOString());
    return ((data ?? []) as Array<{ grade: number; friction: boolean }>)
      .filter((r) => r.grade === -2 || r.grade === -1 || r.grade === 1 || r.grade === 2)
      .map((r) => ({ grade: r.grade as Rep['grade'], friction: !!r.friction }));
  };
}

/** Log one manual rep (+1) or slip (−1) for a tool, now. */
export async function logManualRep(
  supabase: SupabaseClient,
  userId: string,
  toolId: DmtToolId,
  grade: 1 | -1,
): Promise<void> {
  await supabase.from('dmt_events').insert({
    user_id: userId,
    tool_id: toolId,
    occurred_at: new Date().toISOString(),
    grade,
    friction: false,
    source: 'manual',
  });
}

/**
 * Registry of per-tool derivers. The starter five plus the two tools whose
 * telemetry is self-contained (METIS agenda, ARGOS sessions). Tools without a
 * deriver simply don't tally (their line moves only via intake + certification).
 */
export const DERIVERS: Partial<Record<DmtToolId, RepDeriver>> = {
  themis: deriveThemisReps,
  kyrs: deriveKyrsReps,
  clio: deriveClioReps,
  metis: deriveMetisReps,
  argos: deriveArgosReps,
  telos: manualDeriverFor('telos'),
  numa: manualDeriverFor('numa'),
};

// ---------------------------------------------------------------------------
// Weekly tally (idempotent)
// ---------------------------------------------------------------------------

/** Reveal history for securing/maintenance, read from prior `dmt_reveals`. */
async function loadRevealHistory(
  supabase: SupabaseClient,
  userId: string,
  toolId: DmtToolId,
  windowRepCount: number,
  windowFrictionReps: number,
  certified: boolean,
): Promise<RevealHistory> {
  const { data } = await supabase
    .from('dmt_reveals')
    .select('new_position')
    .eq('user_id', userId)
    .eq('tool_id', toolId)
    .order('week_start', { ascending: true })
    .limit(SECURE_CONSECUTIVE_REVEALS);

  const rows = (data ?? []) as Array<{ new_position: number }>;
  const recent = rows.map((r) => r.new_position).slice(-(SECURE_CONSECUTIVE_REVEALS - 1));
  const last = recent.length ? recent[recent.length - 1] : 0;

  return {
    recentDisplayed: recent,
    windowReps: windowRepCount,
    windowFrictionReps,
    certified, // real per-tool certification flag from dmt_state
    wasBelowOneLastReveal: last < 1,
  };
}

/**
 * Run the weekly tally for one tool for the week starting `mondayStart`.
 * Idempotent: the `dmt_reveals` unique key on (user, tool, week_start) means a
 * second call for the same week is a no-op. Persists updated state + a reveal
 * row (marked unseen). Returns true if a new reveal was written.
 */
export async function tallyToolWeek(
  supabase: SupabaseClient,
  userId: string,
  toolId: DmtToolId,
  mondayStart: Date,
  alreadyTallied?: Set<string>,
): Promise<boolean> {
  const wkKey = weekKey(mondayStart);

  // Idempotency guard. Callers that already know which (tool, week) combos
  // exist (e.g. runWeeklyTally's batched pre-fetch) pass `alreadyTallied` to
  // skip this round trip; standalone callers fall back to a direct check.
  if (alreadyTallied) {
    if (alreadyTallied.has(`${toolId}|${wkKey}`)) return false;
  } else {
    const { data: existing } = await supabase
      .from('dmt_reveals')
      .select('id')
      .eq('user_id', userId)
      .eq('tool_id', toolId)
      .eq('week_start', wkKey)
      .maybeSingle();
    if (existing) return false;
  }

  const deriver = DERIVERS[toolId];
  if (!deriver) return false;

  const from = mondayStart;
  const to = addWeeksLocal(mondayStart, 1);
  const reps = await deriver(supabase, userId, from, to);

  const { state: prev, certified } = await loadState(supabase, userId, toolId);
  const frictionReps = reps.filter((r) => r.friction).length;
  const history = await loadRevealHistory(
    supabase,
    userId,
    toolId,
    prev.repCount + reps.length,
    frictionReps,
    certified,
  );

  const now = new Date().toISOString();
  const result = computeReveal(prev, { reps }, history, now);

  // Persist events (one dmt_events row per rep) then state. Manual-evidence
  // tools already wrote their events when the user logged them.
  if (reps.length > 0 && !MANUAL_ONLY_TOOLS.has(toolId)) {
    await supabase.from('dmt_events').insert(
      reps.map((r) => ({
        user_id: userId,
        tool_id: toolId,
        occurred_at: to.toISOString(),
        grade: r.grade,
        friction: r.friction,
        source: 'derived',
      })),
    );
  }
  await persistState(supabase, userId, toolId, result.state);

  // Only surface a reveal when something actually happened this week: reps
  // came in, the displayed point moved, or a securing/at-risk/relapse event
  // fired. A silent no-opportunity week updates state (for drift) but does NOT
  // dump a "held at 0 — nothing came up" card on the user.
  const worthRevealing =
    reps.length > 0 || result.newDisplayed !== result.oldDisplayed || result.event !== null;
  if (!worthRevealing) return false;

  await supabase.from('dmt_reveals').insert({
    user_id: userId,
    tool_id: toolId,
    week_start: wkKey,
    old_position: result.oldDisplayed,
    new_position: result.newDisplayed,
    rolling_before: result.rollingBefore,
    rolling_after: result.rollingAfter,
    report: buildReport(toolId, result.oldDisplayed, result.newDisplayed, reps.length, result.event),
    event: result.event,
  });
  return true;
}

/**
 * Catch-up tally: on app open, tally completed weeks not yet revealed, for all
 * tools with a deriver. Lazy — no cron. Bounded to the trailing 6 weeks.
 *
 * A tool is only tallied for weeks at/after its intake was taken — before the
 * user has placed a line there is nothing to progress, and back-filling empty
 * historical weeks would manufacture entropy-drift reveals from thin air (the
 * "13 no-op cards on first open" bug). No intake for a tool → it isn't tallied
 * until intake exists.
 */
export async function runWeeklyTally(
  supabase: SupabaseClient,
  userId: string,
  now = new Date(),
  game: GameSettings = DEFAULT_GAME_SETTINGS,
): Promise<void> {
  // The week closes at the Sunday-evening boundary session (ruling 1.11): from
  // that evening on, the week just ended counts as completed.
  const openWeek = weekStart(now, game);
  const currentWeekStart = isWeekBoundaryEvening(now, game) ? addWeeksLocal(openWeek, 1) : openWeek;
  const toolIds = Object.keys(DERIVERS) as DmtToolId[];

  // Earliest intake per tool — the floor below which we never tally.
  const { data: intakes } = await supabase
    .from('dmt_intake')
    .select('tool_id, taken_at')
    .eq('user_id', userId);
  const firstIntake = new Map<string, number>();
  for (const r of (intakes ?? []) as Array<{ tool_id: string; taken_at: string }>) {
    const t = new Date(r.taken_at).getTime();
    const prev = firstIntake.get(r.tool_id);
    if (prev === undefined || t < prev) firstIntake.set(r.tool_id, t);
  }

  // Pre-fetch every (tool, week) combo already tallied in one round trip —
  // without this, the loop below did up to 7 tools × 6 weeks = 42 sequential
  // existence-check queries on every app open, which is what made web
  // refreshes crawl before the shell (or the weekly reveal) ever appeared.
  const { data: revealed } = await supabase
    .from('dmt_reveals')
    .select('tool_id, week_start')
    .eq('user_id', userId);
  const alreadyTallied = new Set(
    ((revealed ?? []) as Array<{ tool_id: string; week_start: string }>).map(
      (r) => `${r.tool_id}|${r.week_start}`,
    ),
  );

  await Promise.all(
    toolIds.map(async (toolId) => {
      const intakeAt = firstIntake.get(toolId);
      if (intakeAt === undefined) return; // no line placed yet → nothing to tally
      // Tally the last up-to-6 completed weeks, but never a week that ended
      // before the user took intake for this tool.
      for (let i = 6; i >= 1; i--) {
        const wk = addWeeksLocal(currentWeekStart, -i);
        const weekEnd = addWeeksLocal(wk, 1).getTime();
        if (weekEnd <= intakeAt) continue; // week fully predates intake
        await tallyToolWeek(supabase, userId, toolId, wk, alreadyTallied);
      }
    }),
  );
}

// ---------------------------------------------------------------------------
// Reveal report copy
// ---------------------------------------------------------------------------

function buildReport(
  toolId: DmtToolId,
  oldPos: number,
  newPos: number,
  repCount: number,
  event: 'secured' | 'at_risk' | 'relapse' | null,
): string {
  const tool = getTool(toolId);
  const malady = tool.maladies[0]?.name ?? 'the demon';
  if (event === 'secured') return `${tool.name} secured. ${malady} no longer holds the seat.`;
  if (event === 'relapse') return `${tool.name} relapsed — ${malady} has reclaimed the seat.`;
  if (event === 'at_risk') return `${tool.name} is at risk. ${malady} is creeping back.`;
  if (repCount === 0) return `${tool.name} held at ${fmt(newPos)} — no opportunity came up this week.`;
  if (newPos > oldPos) return `${tool.name} ${fmt(oldPos)} → ${fmt(newPos)}. ${malady} is losing ground.`;
  if (newPos < oldPos) return `${tool.name} slipped ${fmt(oldPos)} → ${fmt(newPos)}. ${malady} gained ground.`;
  return `${tool.name} held at ${fmt(newPos)} this week.`;
}

function fmt(pos: number): string {
  return pos > 0 ? `+${pos}` : `${pos}`;
}

// ---------------------------------------------------------------------------
// Reveal fetch (for the weekly reveal UI)
// ---------------------------------------------------------------------------

export interface PendingReveal {
  id: number;
  toolId: DmtToolId;
  weekStart: string;
  oldPosition: number;
  newPosition: number;
  report: string;
  event: 'secured' | 'at_risk' | 'relapse' | null;
}

/** Unseen reveals — drives the weekly reveal screen. */
export async function loadPendingReveals(supabase: SupabaseClient, userId: string): Promise<PendingReveal[]> {
  const { data } = await supabase
    .from('dmt_reveals')
    .select('id, tool_id, week_start, old_position, new_position, report, event')
    .eq('user_id', userId)
    .is('seen_at', null)
    .order('week_start', { ascending: true });

  return ((data ?? []) as Array<{
    id: number;
    tool_id: string;
    week_start: string;
    old_position: number;
    new_position: number;
    report: string;
    event: string | null;
  }>).map((r) => ({
    id: r.id,
    toolId: r.tool_id as DmtToolId,
    weekStart: r.week_start,
    oldPosition: r.old_position,
    newPosition: r.new_position,
    report: r.report,
    event: (r.event as PendingReveal['event']) ?? null,
  }));
}

/** Mark reveals seen after the animation plays. */
export async function markRevealsSeen(supabase: SupabaseClient, userId: string, ids: number[]): Promise<void> {
  if (ids.length === 0) return;
  await supabase.from('dmt_reveals').update({ seen_at: new Date().toISOString() }).eq('user_id', userId).in('id', ids);
}
